import type { FC, KeyboardEvent, PointerEvent, ReactNode } from 'react'

import { useState } from 'react'
import { cn } from '@/lib/utils'

const HOURS: Array<number> = Array.from({ length: 25 }, (_, index) => index * 2)

const CORE: Array<number> = [
    18.0, 18.6, 20.1, 23.0, 27.4, 32.6, 38.0, 43.1, 47.6, 51.4, 54.4, 56.6, 57.8,
    58.0, 57.4, 56.3, 54.9, 53.3, 51.6, 49.9, 48.2, 46.6, 45.0, 43.5, 42.1
]

const SURFACE: Array<number> = [
    17.0, 17.3, 18.2, 19.8, 22.1, 25.0, 28.2, 31.4, 34.4, 36.9, 38.8, 40.1, 41.2,
    42.0, 42.2, 42.0, 41.5, 40.8, 40.0, 39.1, 38.2, 37.3, 36.4, 35.5, 34.7
]

const LIMIT = 15
const MAX_HOURS = 48
const LAST_INDEX = HOURS.length - 1
const WIDTH = 400
const HEIGHT = 180
const MIN_TEMP = 10
const MAX_TEMP = 65
const GRID_TEMPS: Array<number> = [15, 30, 45, 60]
const AXIS_HOURS: Array<number> = [0, 12, 24, 36, 48]

interface Point {
    x: number
    y: number
}

const toX = (hours: number): number => hours / MAX_HOURS * WIDTH

const toY = (temperature: number): number => 172 - (temperature - MIN_TEMP) / (MAX_TEMP - MIN_TEMP) * 164

const toPoints = (values: Array<number>): Array<Point> => {
    return values.map((value, index) => ({ x: toX(HOURS[index]), y: toY(value) }))
}

const format = (value: number): string => value.toFixed(2)

const smoothSegments = (points: Array<Point>): string => {
    let path = ''

    for (let index = 0; index < points.length - 1; index++) {
        const previous = points[index - 1] ?? points[index]
        const current = points[index]
        const next = points[index + 1]
        const after = points[index + 2] ?? next
        const firstControl = { x: current.x + (next.x - previous.x) / 6, y: current.y + (next.y - previous.y) / 6 }
        const secondControl = { x: next.x - (after.x - current.x) / 6, y: next.y - (after.y - current.y) / 6 }

        path += ` C${format(firstControl.x)} ${format(firstControl.y)} ${format(secondControl.x)} ${format(secondControl.y)} ${format(next.x)} ${format(next.y)}`
    }

    return path
}

const smoothPath = (points: Array<Point>): string => {
    return `M${format(points[0].x)} ${format(points[0].y)}${smoothSegments(points)}`
}

const findCrossing = (direction: 'up' | 'down'): number => {
    for (let index = 0; index < LAST_INDEX; index++) {
        const current = CORE[index] - SURFACE[index]
        const next = CORE[index + 1] - SURFACE[index + 1]
        const crosses = direction === 'up'
            ? current < LIMIT && next >= LIMIT
            : current >= LIMIT && next < LIMIT

        if (crosses) return HOURS[index] + (LIMIT - current) / (next - current) * (HOURS[index + 1] - HOURS[index])
    }

    return direction === 'up' ? 0 : MAX_HOURS
}

const CORE_POINTS = toPoints(CORE)
const SURFACE_POINTS = toPoints(SURFACE)
const CORE_PATH = smoothPath(CORE_POINTS)
const SURFACE_PATH = smoothPath(SURFACE_POINTS)
const BAND_PATH = `${CORE_PATH} L${format(SURFACE_POINTS[LAST_INDEX].x)} ${format(SURFACE_POINTS[LAST_INDEX].y)}${smoothSegments([...SURFACE_POINTS].reverse())} Z`
const ZONE_START = findCrossing('up')
const ZONE_END = findCrossing('down')

const decimal = (value: number): string => value.toFixed(1).replace('.', ',')

const percent = (value: number, total: number): string => `${(value / total * 100).toFixed(3)}%`

const describe = (index: number): string => {
    const difference = CORE[index] - SURFACE[index]

    return `t plus ${HOURS[index]} Stunden: Kern ${decimal(CORE[index])} Grad Celsius, Oberfläche ${decimal(SURFACE[index])} Grad Celsius, Differenz ${decimal(difference)} Kelvin`
}

export const TemperatureChart: FC = (): ReactNode => {
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const index = activeIndex ?? LAST_INDEX
    const difference = CORE[index] - SURFACE[index]
    const overLimit = difference >= LIMIT
    const activeX = toX(HOURS[index])

    const selectFromPointer = (event: PointerEvent<HTMLDivElement>) => {
        const bounds = event.currentTarget.getBoundingClientRect()
        const ratio = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1)

        setActiveIndex(Math.round(ratio * LAST_INDEX))
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const keys: Record<string, number> = {
            ArrowRight: Math.min(index + 1, LAST_INDEX),
            ArrowUp: Math.min(index + 1, LAST_INDEX),
            ArrowLeft: Math.max(index - 1, 0),
            ArrowDown: Math.max(index - 1, 0),
            Home: 0,
            End: LAST_INDEX
        }

        if (!(event.key in keys)) return

        event.preventDefault()
        setActiveIndex(keys[event.key])
    }

    return (
        <figure className='m-0'>
            <figcaption className='sr-only'>
                Beispielverlauf einer Bodenplatte über 48 Stunden: Die Kerntemperatur steigt bis etwa 58 Grad Celsius, die Oberfläche bis etwa 42 Grad Celsius. Zwischen Stunde 19 und Stunde 28 liegt die Kern-Rand-Differenz über dem Grenzwert von 15 Kelvin.
            </figcaption>

            <div className='relative ml-8 mb-7 mt-9'>
                <div
                    role='slider'
                    tabIndex={0}
                    aria-label='Zeitpunkt im Temperaturverlauf'
                    aria-valuemin={0}
                    aria-valuemax={MAX_HOURS}
                    aria-valuenow={HOURS[index]}
                    aria-valuetext={describe(index)}
                    onPointerDown={selectFromPointer}
                    onPointerMove={selectFromPointer}
                    onPointerLeave={(event) => {
                        if (event.pointerType === 'mouse') setActiveIndex(null)
                    }}
                    onKeyDown={handleKeyDown}
                    className='relative cursor-crosshair touch-pan-y rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-card'
                >
                    <svg
                        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                        aria-hidden='true'
                        className='block h-auto w-full overflow-visible'
                    >
                        <defs>
                            <clipPath id='chart-limit-zone'>
                                <rect
                                    x={format(toX(ZONE_START))}
                                    y='0'
                                    width={format(toX(ZONE_END) - toX(ZONE_START))}
                                    height={HEIGHT}
                                />
                            </clipPath>
                        </defs>

                        {GRID_TEMPS.map((temperature) => (
                            <line
                                key={temperature}
                                x1='0'
                                x2={WIDTH}
                                y1={format(toY(temperature))}
                                y2={format(toY(temperature))}
                                className='stroke-border'
                                strokeWidth='1'
                                vectorEffect='non-scaling-stroke'
                            />
                        ))}

                        <g className='chart-wipe'>
                            <path
                                d={BAND_PATH}
                                clipPath='url(#chart-limit-zone)'
                                className='fill-signal/15'
                            />

                            <path
                                d={SURFACE_PATH}
                                fill='none'
                                className='stroke-chart-2'
                                strokeWidth='2'
                                strokeDasharray='5 5'
                                strokeLinecap='round'
                                vectorEffect='non-scaling-stroke'
                            />

                            <path
                                d={CORE_PATH}
                                fill='none'
                                className='stroke-primary'
                                strokeWidth='2.5'
                                strokeLinecap='round'
                                vectorEffect='non-scaling-stroke'
                            />
                        </g>

                        <line
                            x1={format(toX(ZONE_START))}
                            x2={format(toX(ZONE_START))}
                            y1='0'
                            y2={HEIGHT}
                            className='stroke-signal'
                            strokeWidth='1.25'
                            strokeDasharray='3 4'
                            vectorEffect='non-scaling-stroke'
                        />

                        <line
                            x1={format(activeX)}
                            x2={format(activeX)}
                            y1='0'
                            y2={HEIGHT}
                            className={cn('stroke-foreground/30 transition-opacity', activeIndex === null && 'opacity-0')}
                            strokeWidth='1'
                            vectorEffect='non-scaling-stroke'
                        />

                        <circle
                            cx={format(activeX)}
                            cy={format(toY(SURFACE[index]))}
                            r='4'
                            className='fill-card stroke-chart-2'
                            strokeWidth='2'
                            vectorEffect='non-scaling-stroke'
                        />

                        <circle
                            cx={format(activeX)}
                            cy={format(toY(CORE[index]))}
                            r='4.5'
                            className='fill-primary stroke-card'
                            strokeWidth='2'
                            vectorEffect='non-scaling-stroke'
                        />
                    </svg>

                    <span
                        className='pointer-events-none absolute -top-8 inline-flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-md bg-ink px-2 py-1 text-[0.7rem] font-medium text-ink-foreground shadow-sm'
                        style={{ left: percent(toX(ZONE_START), WIDTH) }}
                    >
                        <span className='size-1.5 rounded-full bg-signal' aria-hidden='true' />
                        ΔT-Grenzwert erreicht
                    </span>

                    {GRID_TEMPS.map((temperature) => (
                        <span
                            key={temperature}
                            className='pointer-events-none absolute right-[calc(100%+0.5rem)] -translate-y-1/2 font-mono text-[0.68rem] tabular-nums text-muted-foreground'
                            style={{ top: percent(toY(temperature), HEIGHT) }}
                        >
                            {temperature}
                        </span>
                    ))}

                    {AXIS_HOURS.map((hours) => (
                        <span
                            key={hours}
                            className={cn(
                                'pointer-events-none absolute top-[calc(100%+0.5rem)] whitespace-nowrap font-mono text-[0.68rem] tabular-nums text-muted-foreground',
                                hours === 0 && 'translate-x-0',
                                hours === MAX_HOURS && '-translate-x-full',
                                hours !== 0 && hours !== MAX_HOURS && '-translate-x-1/2'
                            )}
                            style={{ left: percent(toX(hours), WIDTH) }}
                        >
                            {hours} h
                        </span>
                    ))}
                </div>
            </div>

            <div className='flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border pt-4 text-[0.8rem]'>
                <span className='font-mono text-[0.7rem] uppercase tracking-[0.14em] text-muted-foreground'>
                    {activeIndex === null ? 'Aktuell' : `t + ${HOURS[index]} h`}
                </span>

                <span className='inline-flex items-center gap-1.5 font-medium text-foreground'>
                    <span className='size-2 rounded-full bg-primary' aria-hidden='true' />
                    Kern <span className='tabular-nums'>{decimal(CORE[index])} °C</span>
                </span>

                <span className='inline-flex items-center gap-1.5 font-medium text-foreground'>
                    <span className='size-2 rounded-full border-2 border-dashed border-chart-2' aria-hidden='true' />
                    Oberfläche <span className='tabular-nums'>{decimal(SURFACE[index])} °C</span>
                </span>

                <span
                    className={cn(
                        'ml-auto inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium tabular-nums',
                        overLimit ? 'bg-signal-soft text-signal' : 'bg-accent text-accent-foreground'
                    )}
                >
                    ΔT {decimal(difference)} K
                    <span className='font-normal'>
                        {overLimit ? '· über Grenzwert' : '· im Rahmen'}
                    </span>
                </span>
            </div>
        </figure>
    )
}
